import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import { showNotification } from "@/redux/actions/notification";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { identifyUser, ANALYTICS_USER_TYPES } from "@/utils/analytics";

export const lenderHubProfileService = createApi({
  reducerPath: "lenderHubProfileService",
  tagTypes: ["LenderHubProfile"],
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/capworks/",
    credentials: "include",
    prepareHeaders: (headers, { endpoint }) => {
      // Only set Content-Type for non-FormData requests
      if (endpoint !== "updateAvatar") {
        headers.set("Content-Type", "application/json");
      }
      return headers;
    },
  }),
  endpoints: builder => ({
    getProfile: builder.query({
      query: () => ({
        url: "profile",
        method: "GET",
      }),
      providesTags: ["LenderHubProfile"],
      transformResponse: response => {
        // Add timestamp to avatarUrl for cache busting if it exists
        const transformedResponse = response?.avatarUrl
          ? {
              ...response,
              avatarUrl: `${response.avatarUrl}?t=${Date.now()}`,
            }
          : response;

        // Identify user for analytics tracking
        if (response && typeof window !== "undefined") {
          identifyUser({
            userId: response.lenderId,
            userType: ANALYTICS_USER_TYPES.LENDER,
            email: response.email,
            firstName: response.firstName,
            lastName: response.lastName,
            fullName: response.fullName || `${response.firstName || ""} ${response.lastName || ""}`.trim(),
            companyId: undefined,
            companyName: response.contactCompanyName,
            programCount: response.programCount,
          });
        }

        return transformedResponse;
      },
    }),
    updateProfile: builder.mutation({
      query: profileData => ({
        url: "profile",
        method: "PUT",
        body: profileData,
      }),
      invalidatesTags: ["LenderHubProfile"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Profile information has been updated!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          // Handle errors if needed
          Bugsnag.notify(err);
        }
      },
    }),
    updateAvatar: builder.mutation({
      query: file => {
        const formData = new FormData();
        formData.append("File", file);

        return {
          url: "profile/avatar",
          method: "PATCH",
          body: formData,
        };
      },
      async onQueryStarted(file, { dispatch, queryFulfilled }) {
        // Create optimistic avatar URL with timestamp to force refresh
        const optimisticAvatarUrl = file instanceof File ? URL.createObjectURL(file) : null;

        // Optimistically update the profile query in the cache
        const patchResults = [];

        if (optimisticAvatarUrl) {
          // Update the main getProfile query
          const mainPatch = dispatch(
            lenderHubProfileService.util.updateQueryData("getProfile", undefined, draft => {
              if (draft) {
                // Update avatar URL with timestamp to force cache bust

                draft.avatarUrl = `${optimisticAvatarUrl}?t=${Date.now()}`;
              }
            }),
          );
          patchResults.push(mainPatch);
        }

        try {
          const { data } = await queryFulfilled;

          // Clean up the object URL to prevent memory leaks
          if (optimisticAvatarUrl) {
            URL.revokeObjectURL(optimisticAvatarUrl);
          }

          // Update with the actual server response
          dispatch(
            lenderHubProfileService.util.updateQueryData("getProfile", undefined, draft => {
              if (draft && data) {
                // Update with server response, adding timestamp for cache busting

                draft.avatarUrl = data.avatarUrl ? `${data.avatarUrl}?t=${Date.now()}` : draft.avatarUrl;

                // Update any other profile fields that might have changed
                Object.keys(data).forEach(key => {
                  if (data[key] !== undefined) {
                    draft[key] = data[key];
                  }
                });
              }
            }),
          );

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Profile picture has been updated!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch {
          // Revert ALL optimistic updates on error
          patchResults.forEach(patchResult => {
            if (patchResult?.undo) {
              patchResult.undo();
            }
          });

          // Clean up the object URL
          if (optimisticAvatarUrl) {
            URL.revokeObjectURL(optimisticAvatarUrl);
          }

          // Invalidate tags to ensure fresh data on retry
          dispatch(lenderHubProfileService.util.invalidateTags(["LenderHubProfile"]));
        }
      },
      invalidatesTags: ["LenderHubProfile"],
    }),
    deactivateAccount: builder.mutation({
      query: deactivationData => ({
        url: "profile/deactivate",
        method: "POST",
        body: deactivationData,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Your account has been deactivated. You will be logged out shortly.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Failed to deactivate account. Please try again or contact support.",
                options: {
                  variant: NOTIFICATION_VARIANTS.ERROR,
                },
              },
            }),
          );
        }
      },
    }),
  }),
});

export const { useGetProfileQuery, useUpdateProfileMutation, useUpdateAvatarMutation, useDeactivateAccountMutation } =
  lenderHubProfileService;
